"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const vue_1 = require("vue");
const store_1 = __importDefault(require("@/store"));
const Map_1 = require("@/classes/Map");
const action_types_1 = require("@/enums/action-types");
class WorldMap {
    constructor(emit) {
        this.emit = emit;
        this.countries = store_1.default.getters.countries();
        this.countryIsoSelected = (0, vue_1.ref)(null);
        this.hoveredCountryIso = (0, vue_1.ref)(null);
        this.map = (0, vue_1.ref)(new Map_1.Map(930, 400, ['x', 'y'], 1, 62.32108624005466, 0, new Map_1.Zoom(1.6, 1 / 1.6, -120, -57, 1), new Map_1.Dragging(false, 0, 0, 0, 0), new Map_1.LabelCoordinates(0, 0)));
        /**
         * Get details about the hovered country, if one is being hovered.
         *
         * @returns {ICountry | undefined}
         */
        this.hoveredCountry = (0, vue_1.computed)(() => {
            return this.countries.find(country => country.iso === this.hoveredCountryIso.value);
        });
    }
    mounted() {
        this.setBrowserWidthBasedValues();
        document.addEventListener('mouseup', this.stopMapDraggingInteraction.bind(this));
        this.initiateInitialZooming();
    }
    onBeforeUnmount() {
        // Remove the event listener that was added to handle beyond-map-border-mouse-release interactions.
        document.removeEventListener('mouseup', this.stopMapDraggingInteraction.bind(this));
    }
    initiateInitialZooming() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!window.Shared || typeof window.Shared.breakpoints !== 'function') {
                return;
            }
            // Only zoom in on the expected user location on smaller breakpoints.
            window.Shared.breakpoints(['Small', 'Medium'], this.handleInitialZooming.bind(this));
        });
    }
    handleInitialZooming() {
        const locationSelector = document.querySelector('.js-locationSelect');
        if (!locationSelector) {
            return;
        }
        const locations = Array.from(locationSelector.children);
        const selectedCountry = locations.find(location => location.value === locationSelector.value);
        if (!selectedCountry) {
            return;
        }
        const expectedUserCountry = this.countries.find(country => country.iso === selectedCountry.dataset.iso);
        if (!expectedUserCountry) {
            return;
        }
        this.zoomInOnCountry(expectedUserCountry);
    }
    zoomInOnCountry(country) {
        // Prepare an object that simulates a click on the "zoom in" button.
        const zoomPayload = {
            target: {
                dataset: {
                    zoomAction: "zoomIn"
                }
            },
            stopPropagation: () => { }
        };
        // Zoom in a few times, so that the suggested country is more visible.
        this.mapZoom(zoomPayload);
        this.mapZoom(zoomPayload);
        // On mobile, zoom in a third time.
        window.Shared.breakpoints(['Small'], () => this.mapZoom(zoomPayload));
        // Move to the right location on the map.
        this.map.value.xCoordinate = ((((country.lon * -1) - 180) * 2.536) + 178);
        this.map.value.yCoordinate = ((((country.lat) + 90) * 3.011) - 447);
    }
    /**
     * Update dragging-related variables to mark that a dragging interaction
     * has started.
     *
     * @param {event} event
     * @return void
     */
    startMapDraggingInteraction(event) {
        this.map.value.dragging.active = true;
        // Set the initial dragging-related coordinates.
        this.map.value.coordinates.forEach(coordinate => {
            this.map.value.dragging[`${coordinate}Start`] =
                this.map.value.dragging[`${coordinate}Current`] =
                    event[`offset${coordinate.toUpperCase()}`];
        });
    }
    startMapDraggingInteractionTouch(event) {
        this.map.value.dragging.active = true;
        // Set the initial dragging-related coordinates.
        this.map.value.coordinates.forEach(coordinate => {
            this.map.value.dragging[`${coordinate}Start`] =
                this.map.value.dragging[`${coordinate}Current`] =
                    event.changedTouches[0][`client${coordinate.toUpperCase()}`];
        });
    }
    /**
     * Update dragging-related variables to mark that a dragging interaction
     * has stopped.
     *
     * @param {event} event
     * @return void
     */
    stopMapDraggingInteraction(event) {
        return __awaiter(this, void 0, void 0, function* () {
            if (event && event.target && event.target.tagName === 'BUTTON') {
                return;
            }
            // Stop dragging interaction on next tick, to allow differentiation between dragging and clicking.
            yield (0, vue_1.nextTick)();
            this.stopMapDragging();
        });
    }
    stopMapDragging() {
        setTimeout(() => {
            this.map.value.dragging.active = false;
            // Reset dragging-related coordinates.
            this.map.value.coordinates.forEach(coordinate => {
                this.map.value[`${coordinate}Coordinate`] = this.getMapPosition(coordinate);
                this.map.value.dragging[`${coordinate}Start`] = this.map.value.dragging[`${coordinate}Current`] = 0;
            });
        }, 0);
    }
    /**
     * Get the map position in a certain direction ('horizontal' when
     * coordinate is 'x' or 'vertical' when coordinate is 'y').
     *
     * @param {string} coordinate
     * @return number
     */
    getMapPosition(coordinate) {
        return this.map.value[`${coordinate}Coordinate`] -
            this.map.value.dragging[`${coordinate}Start`] / this.map.value.scale +
            this.map.value.dragging[`${coordinate}Current`] / this.map.value.scale;
    }
    /**
     * When a dragging-interaction is active, update the coordinates that
     * are used to re-position the map.
     *
     * @param {event} event
     * @return void
     */
    updateMapPosition(event) {
        if (this.hoveredCountryIso.value) {
            // Update the coordinates where the label of the hovered country should be shown.
            this.map.value.coordinates.forEach(coordinate => {
                this.map.value.labelCoordinates[coordinate] = event[`offset${coordinate.toUpperCase()}`];
            });
        }
        // The map position should only be modified when a dragging interaction is active.
        if (!this.map.value.dragging.active) {
            return;
        }
        // Update the current x coordinate based on the event-offset information.
        this.map.value.coordinates.forEach(coordinate => {
            this.map.value.dragging[`${coordinate}Current`] = event[`offset${coordinate.toUpperCase()}`];
        });
    }
    updateMapPositionTouch(event) {
        if (this.hoveredCountryIso.value) {
            // Update the coordinates where the label of the hovered country should be shown.
            this.map.value.coordinates.forEach(coordinate => {
                this.map.value.labelCoordinates[coordinate] = event.changedTouches[0][`client${coordinate.toUpperCase()}`];
            });
        }
        // The map position should only be modified when a dragging interaction is active.
        if (!this.map.value.dragging.active) {
            return;
        }
        // Update the current x coordinate based on the event-offset information.
        this.map.value.coordinates.forEach(coordinate => {
            this.map.value.dragging[`${coordinate}Current`] = event.changedTouches[0][`client${coordinate.toUpperCase()}`];
        });
    }
    /**
     * Zoom in or out, depending on the requested action, by updating the
     * map scale and coordinates.
     *
     * @param {event} event
     * @return void
     */
    mapZoom(event) {
        event.stopPropagation();
        if (!event || !event.target || !event.target.dataset || !event.target.dataset.zoomAction) {
            return;
        }
        const action = event.target.dataset.zoomAction;
        const scaleAfterZoom = this.map.value.scale * this.map.value.zoom[action];
        // Don't allow too much zooming in or out.
        if (scaleAfterZoom > 6 || scaleAfterZoom < this.map.value.zoom.min) {
            return;
        }
        this.map.value.scale = scaleAfterZoom;
        this.mapZoomUpdateCoordinates(action);
    }
    /**
     * In line with zooming in or out, update the coordinates that are
     * used for positioning the map.
     *
     * @param {string} action - 'zoomIn' or 'zoomOut'
     * @return void
     */
    mapZoomUpdateCoordinates(action) {
        this.map.value.coordinates.forEach(coordinate => {
            // When zooming out, update the zoom values before updating the coordinates.
            if (action === action_types_1.ActionType.ZOOM_OUT) {
                this.updateZoomCoordinateFactors(coordinate, action);
            }
            const change = this.map.value.zoom[`${coordinate}Change`] * this.map.value.zoom.zoomIn;
            const effect = action === action_types_1.ActionType.ZOOM_IN ? 1 : -1;
            // The coordinates that the map is placed at should be updated to zoom in/out on the current center of the map.
            this.map.value[`${coordinate}Coordinate`] += change * effect;
            // When zooming in, update the zoom values after updating the coordinates.
            if (action === action_types_1.ActionType.ZOOM_IN) {
                this.updateZoomCoordinateFactors(coordinate, action);
            }
        });
    }
    /**
     * Update the factors that are used to update the coordinates when a
     * dragging interaction is active, to ensure that the action movement
     * when dragging the map will stay in line with the mouse movement.
     *
     * @param {string} coordinate - 'x' or 'y'
     * @param {string} action - 'zoomIn' or 'zoomOut'
     */
    updateZoomCoordinateFactors(coordinate, action) {
        this.map.value.zoom[`${coordinate}Change`] /= this.map.value.zoom[action];
    }
    /**
     * Event listener that is triggered when a country is hovered, and
     * which sets the 'hovered' variable.
     *
     * @param {event} event
     * @return void
     */
    setAsHovered(event) {
        this.setPropertyOnAction(event, action_types_1.ActionType.HOVERED);
    }
    /**
     * Event listener that is triggered when a country is no longer hovered,
     * and which unsets the 'hovered' variable.
     *
     * @param {event} event
     * @return void
     */
    setAsUnHovered(event) {
        this.setPropertyOnAction(event, action_types_1.ActionType.UNHOVERED);
    }
    /**
     * Event listener that is triggered when a country is selected, and
     * which sets the 'selected' variable.
     *
     * @param {event} event
     * @return void
     */
    setAsSelected(event) {
        // If the coordinates have changed too much, it's not a click interaction, so stop here.
        if (this.hasMouseMoved()) {
            return;
        }
        this.setPropertyOnAction(event, action_types_1.ActionType.SELECTED);
    }
    /**
     * Function that answers the question whether the mouse has moved, to
     * help determine whether an interaction was a drag or click interaction.
     *
     * @return void
     */
    hasMouseMoved() {
        let moved = false;
        // If either of the coordinates has changed too much, the mouse was moved.
        this.map.value.coordinates.forEach(coordinate => {
            if (Math.abs(this.map.value.dragging[`${coordinate}Start`] - this.map.value.dragging[`${coordinate}Current`]) > 3) {
                moved = true;
            }
        });
        return moved;
    }
    /**
     * Update variables that are used to style the map differently based on
     * user interactions (hovering / clicking).
     *
     * @param {event} event
     * @param {string} action
     * @return void
     */
    setPropertyOnAction(event, action) {
        if (!event || !event.target || !event.target.dataset) {
            console.warn(`WorldMap.setPropertyOnAction: information missing on ${action} action.`, event);
            return;
        }
        // When we're trying to unset the hovered variable, make sure it wasn't already updated to a different country.
        if (action === action_types_1.ActionType.UNHOVERED) {
            if (this.hoveredCountryIso.value === event.target.dataset.iso) {
                this.hoveredCountryIso.value = null;
            }
            return;
        }
        if (action === action_types_1.ActionType.HOVERED) {
            this.hoveredCountryIso.value = event.target.dataset.iso;
        }
        if (action === action_types_1.ActionType.SELECTED) {
            this.countryIsoSelected.value = event.target.dataset.iso;
            this.emitResponseIfSelected(event.target.dataset.iso);
        }
    }
    /**
     * After a delay, announce that the user has selected a country.
     *
     * @param {string} iso
     * @return void
     */
    emitResponseIfSelected(iso) {
        setTimeout(() => {
            this.emit('countrySelected', iso);
        }, 1000);
    }
    setBrowserWidthBasedValues() {
        const width = window.innerWidth;
        if (width < 480) {
            this.map.value.yCoordinate = 200;
        }
        else if (width < 720) {
            this.map.value.width = width;
            this.map.value.height = width / 6 + 220;
            this.map.value.scale = width / 2300 + 0.18;
            this.map.value.zoom.min = width / 1500;
            this.map.value.yCoordinate = 100;
        }
        else if (width < 1000) {
            this.map.value.width = 720;
            this.map.value.height = 350;
            this.map.value.scale = 0.7;
            this.map.value.zoom.min = 0.6;
        }
        else {
            this.map.value.width = 930;
            this.map.value.height = 400;
            this.map.value.scale = 0.9;
            this.map.value.zoom.min = 0.75;
        }
    }
}
exports.default = WorldMap;
